import React, { useState, useEffect } from 'react';
import {
    Card, Typography, Table, Tag, Button, Modal, Form, Input,
    Select, message, Space, Descriptions, Tabs, Popconfirm, Grid, Badge
} from 'antd';
import {
    CheckCircleOutlined, CloseCircleOutlined, EyeOutlined,
    ClockCircleOutlined, ReloadOutlined
} from '@ant-design/icons';
import client from '../api/client';
import dayjs from 'dayjs';
import { useAuth } from '../context/AuthContext';

const { Title, Text } = Typography;
const { TextArea } = Input;

const statusColor = {
    PENDING:  'orange',
    APPROVED: 'green',
    REJECTED: 'red',
};

const typeLabel = (type) => {
    const map = {
        PalayPurchase: 'Purchase',
        SalesOrder:    'Sales Order',
        HaulingOrder:  'Hauling',
        MillingOrder:  'Milling',
        JournalEntry:  'Journal Entry',
    };
    const base = type?.split('\\').pop();
    return map[base] || base || 'Unknown';
};

const ApprovalsPage = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const { user } = useAuth();
    const isManagerOrAdmin = ['ADMIN', 'MANAGER'].includes(user?.role);

    const [approvals, setApprovals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('PENDING');
    const [selectedApproval, setSelectedApproval] = useState(null);
    const [detailVisible, setDetailVisible] = useState(false);
    const [actionForm] = Form.useForm();
    const [actionLoading, setActionLoading] = useState(false);
    const [actionType, setActionType] = useState(null); // 'approve' | 'reject'
    const [actionVisible, setActionVisible] = useState(false);

    useEffect(() => {
        fetchApprovals();
    }, [activeTab]);

    const fetchApprovals = async () => {
        setLoading(true);
        try {
            const { data } = await client.get('/approvals', {
                params: { status: activeTab === 'ALL' ? undefined : activeTab },
            });
            setApprovals(Array.isArray(data) ? data : (data.data || []));
        } catch (err) {
            message.error('Failed to load approvals');
        } finally {
            setLoading(false);
        }
    };

    const viewDetail = async (record) => {
        try {
            const { data } = await client.get(`/approvals/${record.id}`);
            setSelectedApproval(data);
            setDetailVisible(true);
        } catch {
            message.error('Failed to load approval details');
        }
    };

    const openAction = (record, type) => {
        setSelectedApproval(record);
        setActionType(type);
        actionForm.resetFields();
        setActionVisible(true);
    };

    const submitAction = async (values) => {
        setActionLoading(true);
        try {
            const endpoint = `/approvals/${selectedApproval.id}/${actionType}`;
            await client.post(endpoint, { comments: values.comments });
            message.success(actionType === 'approve' ? 'Approval granted.' : 'Request rejected.');
            setActionVisible(false);
            fetchApprovals();
        } catch (err) {
            message.error(err.response?.data?.message || 'Action failed');
        } finally {
            setActionLoading(false);
        }
    };

    const cancelRequest = async (id) => {
        try {
            await client.delete(`/approvals/${id}`);
            message.success('Approval request cancelled.');
            fetchApprovals();
        } catch (err) {
            message.error(err.response?.data?.message || 'Failed to cancel');
        }
    };

    const columns = [
        {
            title: 'Type',
            dataIndex: 'approvable_type',
            render: (type) => <Tag color="blue">{typeLabel(type)}</Tag>,
            width: 130,
        },
        {
            title: 'Record ID',
            dataIndex: 'approvable_id',
            width: 100,
            render: (id) => `#${id}`,
        },
        {
            title: 'Requested By',
            dataIndex: ['requester', 'name'],
            render: (name) => name || '—',
        },
        {
            title: 'Reason',
            dataIndex: 'reason',
            ellipsis: true,
            render: (r) => r || <Text type="secondary">—</Text>,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            width: 110,
            render: (s) => <Tag color={statusColor[s]}>{s}</Tag>,
        },
        {
            title: 'Requested',
            dataIndex: 'created_at',
            width: 160,
            render: (d) => dayjs(d).format('MMM D, YYYY HH:mm'),
        },
        {
            title: 'Action',
            align: 'right',
            width: 220,
            render: (_, record) => (
                <Space>
                    <Button size="small" icon={<EyeOutlined />} onClick={() => viewDetail(record)}>
                        View
                    </Button>
                    {isManagerOrAdmin && record.status === 'PENDING' && (
                        <>
                            <Button
                                size="small"
                                type="primary"
                                icon={<CheckCircleOutlined />}
                                onClick={() => openAction(record, 'approve')}
                            >
                                Approve
                            </Button>
                            <Button
                                size="small"
                                danger
                                icon={<CloseCircleOutlined />}
                                onClick={() => openAction(record, 'reject')}
                            >
                                Reject
                            </Button>
                        </>
                    )}
                    {record.status === 'PENDING' && record.requested_by === user?.id && (
                        <Popconfirm
                            title="Cancel this approval request?"
                            onConfirm={() => cancelRequest(record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button size="small" danger>Cancel</Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    const pendingCount = approvals.filter(a => a.status === 'PENDING').length;

    const tabItems = [
        { key: 'PENDING', label: <Badge count={activeTab === 'PENDING' ? 0 : pendingCount} offset={[10, 0]}><span><ClockCircleOutlined style={{ marginRight: 6 }} />Pending</span></Badge> },
        { key: 'APPROVED', label: <span><CheckCircleOutlined style={{ marginRight: 6 }} />Approved</span> },
        { key: 'REJECTED', label: <span><CloseCircleOutlined style={{ marginRight: 6 }} />Rejected</span> },
        { key: 'ALL', label: 'All' },
    ];

    return (
        <div style={{ padding: isMobile ? '16px' : '24px 32px' }}>
            <div style={{ marginBottom: 32, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <div>
                    <Title level={2} style={{ margin: 0, fontWeight: 800 }}>Approval Requests</Title>
                    <Text style={{ color: 'var(--text-secondary)', fontSize: 16 }}>
                        Review and manage transaction approval requests.
                    </Text>
                </div>
                <Button icon={<ReloadOutlined />} onClick={fetchApprovals} style={{ height: 40, borderRadius: 8 }}>
                    Refresh
                </Button>
            </div>

            <Card className="premium-card" bodyStyle={{ padding: isMobile ? '0 12px 16px 12px' : '0 24px 24px 24px' }}>
                <Tabs
                    activeKey={activeTab}
                    onChange={(k) => setActiveTab(k)}
                    size="large"
                    items={tabItems}
                />
                <Table
                    dataSource={approvals}
                    columns={columns}
                    loading={loading}
                    rowKey="id"
                    pagination={{ defaultPageSize: 20, showSizeChanger: true }}
                    scroll={{ x: 800 }}
                />
            </Card>

            {/* Detail Modal */}
            <Modal
                open={detailVisible}
                onCancel={() => setDetailVisible(false)}
                title="Approval Request Details"
                footer={
                    <Space>
                        {isManagerOrAdmin && selectedApproval?.status === 'PENDING' && (
                            <>
                                <Button
                                    type="primary"
                                    icon={<CheckCircleOutlined />}
                                    onClick={() => { setDetailVisible(false); openAction(selectedApproval, 'approve'); }}
                                >
                                    Approve
                                </Button>
                                <Button
                                    danger
                                    icon={<CloseCircleOutlined />}
                                    onClick={() => { setDetailVisible(false); openAction(selectedApproval, 'reject'); }}
                                >
                                    Reject
                                </Button>
                            </>
                        )}
                        <Button onClick={() => setDetailVisible(false)}>Close</Button>
                    </Space>
                }
                width={560}
            >
                {selectedApproval && (
                    <Descriptions bordered column={1} size="middle" style={{ marginTop: 8 }}>
                        <Descriptions.Item label="Type">{typeLabel(selectedApproval.approvable_type)}</Descriptions.Item>
                        <Descriptions.Item label="Record ID">#{selectedApproval.approvable_id}</Descriptions.Item>
                        <Descriptions.Item label="Status">
                            <Tag color={statusColor[selectedApproval.status]}>{selectedApproval.status}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Requested By">{selectedApproval.requester?.name}</Descriptions.Item>
                        <Descriptions.Item label="Requested On">
                            {dayjs(selectedApproval.created_at).format('MMMM D, YYYY HH:mm')}
                        </Descriptions.Item>
                        <Descriptions.Item label="Reason">{selectedApproval.reason || '—'}</Descriptions.Item>
                        {selectedApproval.approver && (
                            <Descriptions.Item label="Reviewed By">{selectedApproval.approver.name}</Descriptions.Item>
                        )}
                        {selectedApproval.comments && (
                            <Descriptions.Item label="Comments">{selectedApproval.comments}</Descriptions.Item>
                        )}
                    </Descriptions>
                )}
            </Modal>

            {/* Approve / Reject Modal */}
            <Modal
                open={actionVisible}
                onCancel={() => setActionVisible(false)}
                title={actionType === 'approve' ? 'Approve Request' : 'Reject Request'}
                footer={null}
                width={480}
            >
                <Form form={actionForm} onFinish={submitAction} layout="vertical" style={{ marginTop: 16 }}>
                    <Form.Item
                        name="comments"
                        label="Comments"
                        rules={actionType === 'reject' ? [{ required: true, message: 'Please provide a reason for rejection.' }] : []}
                    >
                        <TextArea rows={4} placeholder={actionType === 'reject' ? 'Reason for rejection (required)...' : 'Optional comments...'} />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0 }}>
                        <Space>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={actionLoading}
                                danger={actionType === 'reject'}
                                icon={actionType === 'approve' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                                style={{ height: 40, borderRadius: 8 }}
                            >
                                {actionType === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
                            </Button>
                            <Button onClick={() => setActionVisible(false)} style={{ height: 40, borderRadius: 8 }}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ApprovalsPage;
